import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { prefersReducedMotion } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/** The pool section prints 250 products per page; 4 pages covers 1,000 products. */
const MAX_POOL_PAGES = 4;
/** Stock changes, so a session cache goes stale after a while. */
const CACHE_TTL_MS = 30 * 60 * 1000;
const CACHE_PREFIX = 'sh-encounter-pool:v2:';
const TUMBLE_MS = 1000;
const FLICKER_MS = 70;
/** How long a roll waits for outlines before drawing game pieces in their place. */
const SILHOUETTE_WAIT_MS = 3000;
const IMAGE_WIDTHS = [120, 180, 240, 360, 480];

/* The lineup ("Ghost" style): the pale 35mm human, the minis in card order, then a 20mm d20. */
const HUMAN_MM = 35;
const D20_MM = 20;
const D20_HALF_WIDTH = 8.66;
const PAWN_PATH =
  'M-17 -3Q-17 0 -14 0L14 0Q17 0 17 -3L17 -9Q17 -12 14 -12L13 -12Q10 -40 7 -68L10 -68Q13 -70 10 -72L-10 -72Q-13 -70 -10 -68L-7 -68Q-10 -40 -13 -12L-14 -12Q-17 -12 -17 -9Z';
const D20_FACE = 'M0 -10L8.66 -5L8.66 5L0 10L-8.66 5L-8.66 -5Z';
const D20_LINES =
  'M0 -4.6L4.9 3.3L-4.9 3.3Z M0 -10L0 -4.6 M-8.66 -5L0 -4.6L8.66 -5 M-8.66 -5L-4.9 3.3 M-8.66 5L-4.9 3.3 M0 10L-4.9 3.3 M0 10L4.9 3.3 M8.66 5L4.9 3.3 M8.66 -5L4.9 3.3';
const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * @typedef {{ w: number, h: number, d: string }} Silhouette
 * @typedef {{
 *   h: string,
 *   u: string,
 *   n: string,
 *   v: number,
 *   p: number,
 *   mm: number,
 *   x: boolean,
 *   i?: string,
 *   iw?: number,
 *   ih?: number,
 *   a?: string,
 *   k?: string
 * }} EncounterMini
 */

/**
 * @typedef {object} RandomEncounterRefs
 * @property {HTMLButtonElement} rollButton - The d20 button.
 * @property {HTMLElement} die - The die graphic that tumbles.
 * @property {SVGTextElement} dieNumber - The number on the die (SVG text inside the die drawing).
 * @property {HTMLElement} rollLine - "Roll 14: A Gnome Wizard ... walk into the Hog."
 * @property {HTMLUListElement} cards - The cast cards.
 * @property {HTMLElement} lineup - Holds the to-scale lineup SVG.
 * @property {HTMLElement} hookWrap - "The hook" paragraph.
 * @property {HTMLElement} hook - The hook text.
 * @property {HTMLFormElement} buyForm - Adds the whole encounter (works without JavaScript too).
 * @property {HTMLElement} buyInputs - The form's hidden items[][id] inputs.
 * @property {HTMLButtonElement} addButton - "Add the whole encounter (4)".
 * @property {HTMLElement} total - The price line under the button.
 * @property {HTMLElement} status - The single live region for this section.
 * @property {HTMLTemplateElement} cardTemplate - Markup for one cast card.
 * @property {HTMLScriptElement} strings - Translated UI strings as JSON.
 * @property {HTMLScriptElement} initial - The first deal (rendered in Liquid) and the human outline.
 */

/**
 * "Roll a random encounter". The first deal arrives rendered in Liquid. Rolling the d20 deals four
 * in-stock minis (five on 15 or better) from sections/sh-home-encounter-pool.liquid, fetched on the
 * collection URL through the Section Rendering API and cached per session. The first card is a
 * mini with a story.hook when the pool has one. Outlines for the lineup come from
 * sections/sh-home-encounter-mini.liquid, one small request per dealt mini, cached for the page.
 *
 * "Add the whole encounter" follows product-form.js: POST the items to the cart add route with the
 * cart-items section ids, announce through a CartLinesUpdateEvent whose promise resolves with the
 * refreshed cart and section HTML, then open the cart drawer.
 *
 * @extends {Component<RandomEncounterRefs>}
 */
class RandomEncounterComponent extends Component {
  requiredRefs = [
    'rollButton',
    'die',
    'dieNumber',
    'rollLine',
    'cards',
    'lineup',
    'hookWrap',
    'hook',
    'buyForm',
    'buyInputs',
    'addButton',
    'total',
    'status',
    'cardTemplate',
    'strings',
    'initial',
  ];

  /** @type {AbortController} */
  #abortController = new AbortController();
  /** @type {Promise<EncounterMini[]> | null} */
  #poolPromise = null;
  /** @type {Map<string, Silhouette | null>} */
  #silhouettes = new Map();
  /** @type {EncounterMini[]} */
  #dealt = [];
  /** @type {Silhouette | null} */
  #human = null;
  /** @type {Record<string, string>} */
  #strings = {};
  #rolling = false;
  #adding = false;
  #lastWidth = 0;
  /** @type {number | undefined} */
  #flickerTimer;
  /** @type {number | undefined} */
  #announceTimer;
  /** @type {number | undefined} */
  #drawFrame;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    this.#strings = readStrings(this.refs.strings);
    this.#readInitial();

    this.refs.rollButton.disabled = false;
    this.#lastWidth = 0;
    this.#draw(true);

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => this.#scheduleDraw());
    this.#resizeObserver.observe(this.refs.lineup);

    // Warm the pool when the section comes near the viewport, so the first roll is quick.
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#intersectionObserver?.disconnect();
        this.#loadPool().catch(() => {
          // A failed warm-up is retried on the first roll.
        });
      },
      { rootMargin: '600px 0px' }
    );
    this.#intersectionObserver.observe(this);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#strings = readStrings(this.refs.strings);
    this.#readInitial();
    this.#draw(true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#poolPromise = null;
    this.#rolling = false;
    this.#adding = false;
    this.#stopFlicker();
    window.clearTimeout(this.#announceTimer);
    if (this.#drawFrame !== undefined) window.cancelAnimationFrame(this.#drawFrame);
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
  }

  /**
   * Rolls the d20 and deals a new encounter.
   * Bound declaratively: on:click="/roll".
   */
  async roll() {
    if (this.#rolling) return;
    this.#rolling = true;

    const { rollButton, die, dieNumber } = this.refs;
    const reducedMotion = prefersReducedMotion();

    rollButton.setAttribute('aria-disabled', 'true');
    this.#announce(this.#t('rolling'));
    this.classList.remove('is-nat20');

    die.classList.remove('is-landed', 'is-waiting', 'is-rolling');
    if (!reducedMotion) {
      // Restart the tumble even when the previous roll's class was just removed.
      void die.offsetWidth;
      die.classList.add('is-rolling');
      this.#startFlicker();
    }

    const tumble = reducedMotion ? Promise.resolve() : wait(TUMBLE_MS);
    tumble.then(() => {
      if (this.#rolling && !reducedMotion) {
        die.classList.remove('is-rolling');
        die.classList.add('is-waiting');
      }
    });

    try {
      const [pool] = await Promise.all([this.#loadPool(), tumble]);
      if (!this.isConnected) return;

      const number = randomInt(1, 20);
      const dealt = deal(pool, number >= 15 ? 5 : 4, this.#dealt);

      this.#stopFlicker();
      die.classList.remove('is-rolling', 'is-waiting');
      dieNumber.textContent = String(number);
      if (!reducedMotion) die.classList.add('is-landed');

      if (!dealt.length) {
        this.refs.rollLine.textContent = this.#t('empty');
        this.#announce(this.#t('empty'));
        return;
      }

      if (number === 20) this.classList.add('is-nat20');
      this.#dealt = dealt;
      const rollLine = this.#renderDeal(number, dealt);

      const hook = dealt.find((item) => item.k)?.k;
      this.#announce(hook ? `${rollLine} ${this.#t('hookStatus').replace('__HOOK__', hook)}` : rollLine);

      // The previous lineup stays (dimmed) until the new outlines arrive, so no game pieces flash up.
      this.refs.lineup.setAttribute('aria-busy', 'true');
      await this.#loadSilhouettes(dealt);
      if (!this.isConnected || this.#dealt !== dealt) return;
      this.refs.lineup.removeAttribute('aria-busy');
      this.#draw(true);
    } catch (error) {
      if (isAbortError(error) || !this.isConnected) return;
      console.warn('[sh-random-encounter] Could not deal an encounter:', error);
      this.#stopFlicker();
      die.classList.remove('is-rolling', 'is-waiting');
      this.refs.lineup.removeAttribute('aria-busy');
      this.refs.rollLine.textContent = this.#t('loadError');
      this.#announce(this.#t('loadError'));
    } finally {
      this.#rolling = false;
      rollButton.removeAttribute('aria-disabled');
    }
  }

  /**
   * Adds every dealt mini to the cart, then opens the cart drawer.
   * Bound declaratively on the form: on:submit="/addAll". Without JavaScript the form posts
   * items[][id] / items[][quantity] to the cart add route itself.
   *
   * @param {SubmitEvent} event
   */
  async addAll(event) {
    event.preventDefault();
    if (this.#adding) return;

    const items = this.#dealt.filter((item) => item.v);
    if (!items.length) return;

    const { addButton } = this.refs;
    this.#adding = true;
    addButton.setAttribute('aria-disabled', 'true');
    this.#announce(this.#t('adding'));

    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.v), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    const detailBase = {
      source: 'sh-random-encounter',
      sourceId: this.id,
      itemCount: items.length,
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({ id: item.v, quantity: 1 })),
          sections: sectionIds.join(','),
        }),
      });
      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );

        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detailBase, items: cart.items, didError: true },
        });

        // Usually one of them sold out since the pool was cached; the next roll fetches fresh stock.
        this.#forgetPool();
        this.#announce(this.#t('addError'));
        return;
      }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detailBase, items: cart.items, sections: result.sections, didError: false },
      });

      this.#announce(this.#t('added'));
      await this.#openCart();
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#announce(this.#t('addError'));
    } finally {
      this.#adding = false;
      addButton.removeAttribute('aria-disabled');
    }
  }

  /** Reads the Liquid-rendered first deal, its outlines and the human outline. */
  #readInitial() {
    const initial = parseJson(this.refs.initial.textContent);
    if (!initial || typeof initial !== 'object') return;

    const human = validSilhouette(initial.human);
    if (human) this.#human = human;

    if (Array.isArray(initial.items)) {
      const items = [];
      for (const raw of initial.items) {
        const item = normalizeMini(raw);
        if (!item) continue;
        items.push(item);
        if ('sil' in raw) this.#silhouettes.set(item.h, validSilhouette(raw.sil));
      }
      if (items.length && !this.#rolling) this.#dealt = items;
    }
  }

  /**
   * Renders a deal: roll line, cards, hook, the add form and the total.
   *
   * @param {number} number
   * @param {EncounterMini[]} dealt
   * @returns {string} The roll line.
   */
  #renderDeal(number, dealt) {
    const { rollLine, cards, hookWrap, hook, buyForm, buyInputs, addButton, total } = this.refs;

    const who = this.#who(dealt);
    let line;
    if (number === 20) line = this.#t('natural20');
    else if (number === 1) line = this.#t('natural1');
    else if (dealt.length === 1) line = this.#t('rollLineOne');
    else line = this.#t('rollLine');
    line = line.replace('__NUMBER__', String(number)).replace('__WHO__', who);
    rollLine.textContent = line;

    cards.classList.remove('is-fresh');
    cards.replaceChildren(...dealt.map((item) => this.#buildCard(item)));
    void cards.offsetWidth;
    cards.classList.add('is-fresh');

    const hookText = dealt.find((item) => item.k)?.k ?? '';
    hook.textContent = hookText;
    hookWrap.hidden = !hookText;

    const inputs = [];
    for (const item of dealt) {
      inputs.push(hiddenInput('items[][id]', String(item.v)), hiddenInput('items[][quantity]', '1'));
    }
    buyInputs.replaceChildren(...inputs);
    buyForm.hidden = false;

    addButton.textContent = this.#t('addAll').replace('__COUNT__', String(dealt.length));
    total.textContent = this.#totalLine(dealt);

    return line;
  }

  /** @param {EncounterMini} item */
  #buildCard(item) {
    const fragment = /** @type {DocumentFragment} */ (this.refs.cardTemplate.content.cloneNode(true));
    const card = /** @type {HTMLElement} */ (fragment.querySelector('.sh-encounter__card'));

    const link = card.querySelector('.sh-encounter__card-link');
    if (link instanceof HTMLAnchorElement) link.href = item.u;

    const name = card.querySelector('.sh-encounter__card-name');
    if (name) name.textContent = item.n;

    const media = card.querySelector('.sh-encounter__card-media');
    const image = card.querySelector('.sh-encounter__card-image');
    if (item.i && image instanceof HTMLImageElement) {
      const src = item.i;
      image.src = sizedImage(src, 360);
      image.srcset = IMAGE_WIDTHS.map((width) => `${sizedImage(src, width)} ${width}w`).join(', ');
      if (item.iw && item.ih) {
        image.width = item.iw;
        image.height = item.ih;
      }
      image.alt = item.a || this.#t('cardAlt').replace('__TITLE__', item.n);
    } else {
      image?.remove();
      if (media instanceof HTMLElement) media.dataset.empty = 'true';
    }

    const chip = card.querySelector('.sh-encounter__chip');
    if (chip) chip.textContent = this.#heightLabel(item);

    const price = card.querySelector('.sh-encounter__price');
    if (price) price.textContent = this.#money(item.p);

    return card;
  }

  /**
   * "A Gnome Wizard, a Dwarf Brewmaster and an Owl"
   *
   * @param {EncounterMini[]} items
   */
  #who(items) {
    const pieces = items.map((item, index) => {
      const vowel = /^[aeiou]/i.test(item.n);
      let article;
      if (index === 0) article = vowel ? this.#t('articleAnStart') : this.#t('articleAStart');
      else article = vowel ? this.#t('articleAn') : this.#t('articleA');
      return `${article} ${item.n}`;
    });
    if (pieces.length < 2) return pieces.join('');
    return `${pieces.slice(0, -1).join(', ')} ${this.#t('and')} ${pieces[pieces.length - 1]}`;
  }

  /** @param {EncounterMini[]} items */
  #totalLine(items) {
    const sum = items.reduce((total, item) => total + item.p, 0);
    const total = this.#money(sum);
    const count = items.length;
    const partySize = Number(this.dataset.partySize) || 4;
    // the ladder ("4:20,8:25,12:30"): the highest rung this many minis reach
    let partyPercent = 0;
    for (const rung of (this.dataset.partyTiers || `${partySize}:${this.dataset.partyPercent || 0}`).split(',')) {
      const [size, percent] = rung.split(':').map(Number);
      if (count >= size && percent > 0) partyPercent = percent;
    }

    if (this.dataset.partyEnabled === 'true' && partyPercent > 0 && count >= partySize) {
      // Shopify floors each unit's discount to the cent, so the estimate does too (matches the cart)
      const off = items.reduce((total, item) => total + Math.floor((item.p * partyPercent) / 100), 0);
      const party = sum - off;
      return this.#t('totalParty')
        .replace('__COUNT__', String(count))
        .replace('__TOTAL__', total)
        .replace('__PARTY__', this.#money(party))
        .replace('__SIZE__', String(partySize));
    }

    if (count === 1) return this.#t('totalOne').replace('__TOTAL__', total);
    return this.#t('total').replace('__COUNT__', String(count)).replace('__TOTAL__', total);
  }

  /** @param {EncounterMini} item */
  #heightLabel(item) {
    return this.#t(item.x ? 'heightApprox' : 'height').replace('__SIZE__', formatMm(item.mm));
  }

  /** @param {number} cents */
  #money(cents) {
    const currency = this.dataset.currency || 'USD';
    const format = this.dataset.moneyFormat || '{{amount}}';
    return formatMoney(cents, format, currency);
  }

  /* ---------- the lineup ---------- */

  #scheduleDraw() {
    if (this.#drawFrame !== undefined) window.cancelAnimationFrame(this.#drawFrame);
    this.#drawFrame = window.requestAnimationFrame(() => {
      this.#drawFrame = undefined;
      this.#draw(false);
    });
  }

  /**
   * Draws the dealt minis to one scale, at the box's real pixel width so labels stay 11px on a phone.
   *
   * @param {boolean} force - Redraw even when the width hasn't changed.
   */
  #draw(force) {
    const host = this.refs.lineup;
    const width = Math.floor(host.clientWidth);
    if (!width) return;
    if (!force && width === this.#lastWidth) return;
    this.#lastWidth = width;

    const minis = this.#dealt.filter((item) => item.mm > 0);
    if (!minis.length || !this.#human) {
      host.replaceChildren();
      return;
    }

    host.replaceChildren(this.#buildLineup(Math.max(240, width), minis, this.#human));
  }

  /**
   * @param {number} boxWidth
   * @param {EncounterMini[]} minis
   * @param {Silhouette} human
   * @returns {SVGSVGElement}
   */
  #buildLineup(boxWidth, minis, human) {
    /** @type {{ kind: 'human' | 'mini' | 'd20', mm: number, w: number, item?: EncounterMini, sil?: Silhouette | null }[]} */
    const slots = [{ kind: 'human', mm: HUMAN_MM, w: (human.w / human.h) * HUMAN_MM }];
    for (const item of minis) {
      const sil = this.#silhouettes.get(item.h) ?? null;
      const w = sil ? (sil.w / sil.h) * item.mm : item.mm * 0.34;
      slots.push({ kind: 'mini', mm: item.mm, w, item, sil });
    }
    slots.push({ kind: 'd20', mm: D20_MM, w: D20_HALF_WIDTH * 2 });

    const tallest = Math.max(...slots.map((slot) => slot.mm));
    const pad = 6;
    const gap = 6;
    const minSlot = 30;
    const top = 8;
    const captionHeight = 22;
    const maxPlot = boxWidth < 480 ? 110 : 130;
    const totalWidth = (/** @type {number} */ k) =>
      2 * pad + gap * (slots.length - 1) + slots.reduce((sum, slot) => sum + Math.max(slot.w * k, minSlot), 0);

    let k = maxPlot / tallest;
    for (let step = 0; step < 60 && totalWidth(k) > boxWidth; step++) k *= 0.95;
    const ground = top + tallest * k;

    const svg = svgElement('svg', { role: 'img', focusable: 'false' });
    const shapes = svgElement('g');
    const labels = svgElement('g');

    let x = pad;
    for (const slot of slots) {
      const slotWidth = Math.max(slot.w * k, minSlot);
      const cx = x + slotWidth / 2;
      const left = cx - (slot.w * k) / 2;
      let label = '';
      let labelClass = 'sh-lineup__label';

      if (slot.kind === 'human') {
        shapes.append(
          svgElement('path', {
            class: 'sh-lineup__ghost',
            d: human.d,
            transform: `translate(${f1(left)} ${f1(ground - HUMAN_MM * k)}) scale(${f4((HUMAN_MM * k) / human.h)})`,
            'vector-effect': 'non-scaling-stroke',
          })
        );
        label = this.#t('height').replace('__SIZE__', String(HUMAN_MM));
      } else if (slot.kind === 'd20') {
        const die = svgElement('g', { transform: `translate(${f1(cx)} ${f1(ground - 10 * k)}) scale(${f4(k)})` });
        die.append(
          svgElement('path', { class: 'sh-lineup__die-face', d: D20_FACE, 'vector-effect': 'non-scaling-stroke' }),
          svgElement('path', { class: 'sh-lineup__die-lines', d: D20_LINES, 'vector-effect': 'non-scaling-stroke' })
        );
        shapes.append(die);
        label = this.#t('d20');
      } else if (slot.item) {
        if (slot.sil) {
          shapes.append(
            svgElement('path', {
              class: 'sh-lineup__mini',
              d: slot.sil.d,
              transform: `translate(${f1(left)} ${f1(ground - slot.mm * k)}) scale(${f4((slot.mm * k) / slot.sil.h)})`,
            })
          );
        } else {
          // The game piece is 34 wide and 100 tall, centred on x = 0, feet on y = 0.
          const pawn = svgElement('g', {
            class: 'sh-lineup__mini',
            transform: `translate(${f1(cx)} ${f1(ground)}) scale(${f4((slot.mm * k) / 100)})`,
          });
          pawn.append(svgElement('path', { d: PAWN_PATH }), svgElement('circle', { cx: '0', cy: '-87', r: '13' }));
          shapes.append(pawn);
        }
        label = this.#heightLabel(slot.item);
        labelClass += ' sh-lineup__label--mini';
      }

      const text = svgElement('text', {
        class: labelClass,
        x: f1(cx),
        y: f1(ground + 15),
        'text-anchor': 'middle',
      });
      text.textContent = label;
      labels.append(text);

      x += slotWidth + gap;
    }

    const width = Math.ceil(x - gap + pad);
    const height = Math.ceil(ground + captionHeight);
    svg.setAttribute('width', String(width));
    svg.setAttribute('height', String(height));
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

    const list = minis
      .map((item) => this.#t('lineupItem').replace('__NAME__', item.n).replace('__SIZE__', this.#heightLabel(item)))
      .join(', ');
    svg.setAttribute('aria-label', this.#t('lineupAria').replace('__LIST__', list));

    svg.append(
      svgElement('line', {
        class: 'sh-lineup__ground',
        x1: '0',
        y1: f1(ground),
        x2: String(width),
        y2: f1(ground),
      }),
      shapes,
      labels
    );

    return svg;
  }

  /* ---------- data ---------- */

  /** @returns {Promise<EncounterMini[]>} */
  #loadPool() {
    if (!this.#poolPromise) {
      this.#poolPromise = this.#fetchPool().catch((error) => {
        this.#poolPromise = null;
        throw error;
      });
    }
    return this.#poolPromise;
  }

  #forgetPool() {
    this.#poolPromise = null;
    const base = this.dataset.poolUrl;
    if (!base) return;
    try {
      window.sessionStorage.removeItem(`${CACHE_PREFIX}${base}`);
    } catch {
      // Storage can be blocked; the in-memory pool is already gone.
    }
  }

  /** @returns {Promise<EncounterMini[]>} */
  async #fetchPool() {
    const base = this.dataset.poolUrl;
    const section = this.dataset.poolSection;
    if (!base || !section) return [];

    const cacheKey = `${CACHE_PREFIX}${base}`;
    const cached = readCache(cacheKey);
    if (cached) return cached;

    const { signal } = this.#abortController;
    const first = await fetchPoolPage(base, section, 1, signal);
    const pageCount = Math.min(Math.max(1, first.pages), MAX_POOL_PAGES);
    const rest = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, index) => fetchPoolPage(base, section, index + 2, signal))
    );

    /** @type {EncounterMini[]} */
    const pool = [];
    const seen = new Set();
    for (const page of [first, ...rest]) {
      for (const raw of page.items) {
        const item = normalizeMini(raw);
        if (!item || seen.has(item.h)) continue;
        seen.add(item.h);
        pool.push(item);
      }
    }

    writeCache(cacheKey, pool);
    return pool;
  }

  /**
   * Fetches outlines for minis that don't have one cached yet. Waits up to SILHOUETTE_WAIT_MS, then
   * lets the caller draw game pieces for the stragglers; they're redrawn when they arrive.
   *
   * @param {EncounterMini[]} items
   */
  async #loadSilhouettes(items) {
    const section = this.dataset.miniSection;
    const missing = items.filter((item) => !this.#silhouettes.has(item.h));
    if (!section || !missing.length) return;

    const { signal } = this.#abortController;
    const jobs = missing.map(async (item) => {
      try {
        const data = await fetchMini(item.u, section, signal);
        this.#silhouettes.set(item.h, validSilhouette(data?.sil));
      } catch (error) {
        if (isAbortError(error)) return;
        console.warn('[sh-random-encounter] No outline for', item.h, error);
      }
    });

    const settled = Promise.allSettled(jobs).then(() => {
      if (this.isConnected && this.#dealt === items) {
        this.refs.lineup.removeAttribute('aria-busy');
        this.#draw(true);
      }
    });

    await Promise.race([settled, wait(SILHOUETTE_WAIT_MS)]);
  }

  /* ---------- cart ---------- */

  /**
   * Opens Horizon's cart drawer the way the theme's own cart flow does: the
   * Shopify.actions.openCart handler (configured in standard-actions-override.js),
   * then the drawer element, then the cart page.
   */
  async #openCart() {
    /** @type {(HTMLElement & { open?: () => void, isOpen?: boolean }) | null} */
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer?.isOpen) return;

    const openCart = window.Shopify?.actions?.openCart;
    if (typeof openCart === 'function') {
      try {
        await openCart();
        return;
      } catch (error) {
        console.warn('[sh-random-encounter] openCart failed:', error);
      }
    }

    if (typeof drawer?.open === 'function') {
      drawer.open();
      return;
    }

    window.location.assign(Theme.routes.cart_url);
  }

  /** Same cart refresh path as product-form.js. */
  async #fetchCart() {
    /** @type {(HTMLElement & { fetchCartData?: () => Promise<any> }) | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      if (typeof cartItemsComponent.fetchCartData === 'function') return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /* ---------- small helpers ---------- */

  /** @param {string} message */
  #announce(message) {
    const { status } = this.refs;
    // Clear first so repeating the same sentence is announced again. A timeout (not rAF)
    // so the update still lands when the tab is in the background.
    window.clearTimeout(this.#announceTimer);
    status.textContent = '';
    this.#announceTimer = window.setTimeout(() => {
      status.textContent = message;
    }, 100);
  }

  /** @param {string} key */
  #t(key) {
    return this.#strings[key] ?? '';
  }

  #startFlicker() {
    this.#stopFlicker();
    this.#flickerTimer = window.setInterval(() => {
      this.refs.dieNumber.textContent = String(randomInt(1, 20));
    }, FLICKER_MS);
  }

  #stopFlicker() {
    if (this.#flickerTimer !== undefined) window.clearInterval(this.#flickerTimer);
    this.#flickerTimer = undefined;
  }
}

/**
 * Picks the deal: a storied mini first (a different one from last time when possible), then the
 * rest at random, favoring minis that weren't in the previous deal.
 *
 * @param {EncounterMini[]} pool
 * @param {number} count
 * @param {EncounterMini[]} previous
 * @returns {EncounterMini[]}
 */
function deal(pool, count, previous) {
  if (!pool.length) return [];

  const previousHandles = new Set(previous.map((item) => item.h));
  const storied = pool.filter((item) => item.k);
  const freshStoried = storied.filter((item) => !previousHandles.has(item.h));
  const leads = freshStoried.length ? freshStoried : storied;

  /** @type {EncounterMini[]} */
  const picks = [];
  const lead = leads.length ? leads[randomInt(0, leads.length - 1)] : undefined;
  if (lead) picks.push(lead);

  const rest = shuffle(pool.filter((item) => item !== lead));
  const fresh = rest.filter((item) => !previousHandles.has(item.h));
  const repeats = rest.filter((item) => previousHandles.has(item.h));
  for (const item of [...fresh, ...repeats]) {
    if (picks.length >= count) break;
    picks.push(item);
  }

  return picks;
}

/**
 * @param {string} base - The collection URL.
 * @param {string} section - The pool section's file name.
 * @param {number} page
 * @param {AbortSignal} signal
 * @returns {Promise<{ pages: number, items: any[] }>}
 */
async function fetchPoolPage(base, section, page, signal) {
  const url = new URL(base, window.location.origin);
  url.searchParams.set('section_id', section);
  url.searchParams.set('page', String(page));

  const data = await fetchSectionJson(url, 'script[data-sh-encounter-pool]', signal);
  return {
    pages: Number(data?.pages) || 1,
    items: Array.isArray(data?.items) ? data.items : [],
  };
}

/**
 * @param {string} productUrl
 * @param {string} section - The mini section's file name.
 * @param {AbortSignal} signal
 */
async function fetchMini(productUrl, section, signal) {
  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set('section_id', section);
  return fetchSectionJson(url, 'script[data-sh-encounter-mini]', signal);
}

/**
 * Fetches a section through the Section Rendering API and parses the JSON in one of its script tags.
 * DOMParser never runs the scripts it parses.
 *
 * @param {URL} url
 * @param {string} selector
 * @param {AbortSignal} signal
 */
async function fetchSectionJson(url, selector, signal) {
  const response = await fetch(url.toString(), {
    headers: { Accept: 'text/html' },
    credentials: 'same-origin',
    signal,
  });
  if (!response.ok) throw new Error(`${url.pathname} ${url.search} failed: ${response.status}`);

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const script = doc.querySelector(selector);
  if (!script) throw new Error(`${selector} missing from ${url.pathname}`);
  return parseJson(script.textContent);
}

/**
 * @param {any} raw
 * @returns {EncounterMini | null}
 */
function normalizeMini(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const handle = typeof raw.h === 'string' ? raw.h : '';
  const url = typeof raw.u === 'string' ? raw.u : '';
  const variant = Number(raw.v);
  const price = Number(raw.p);
  const height = Number(raw.mm);
  if (!handle || !url || !variant || !Number.isFinite(price) || !(height > 0)) return null;

  /** @type {EncounterMini} */
  const item = {
    h: handle,
    u: url,
    n: String(raw.n || handle),
    v: variant,
    p: price,
    mm: height,
    x: raw.x === true,
  };
  if (typeof raw.i === 'string' && raw.i) {
    item.i = raw.i;
    item.iw = Number(raw.iw) || 0;
    item.ih = Number(raw.ih) || 0;
  }
  if (typeof raw.a === 'string' && raw.a) item.a = raw.a;
  if (typeof raw.k === 'string' && raw.k.trim()) item.k = raw.k.trim();
  return item;
}

/**
 * @param {any} value
 * @returns {Silhouette | null}
 */
function validSilhouette(value) {
  if (!value || typeof value !== 'object') return null;
  const w = Number(value.w);
  const h = Number(value.h);
  if (!(w > 0) || !(h > 0) || typeof value.d !== 'string' || !value.d) return null;
  return { w, h, d: value.d };
}

/**
 * The `t` filter HTML-escapes non-_html translations (an apostrophe arrives as &#39;), and a JSON
 * script tag doesn't decode entities, so decode them once here before using textContent.
 *
 * @param {HTMLScriptElement} script
 * @returns {Record<string, string>}
 */
function readStrings(script) {
  const parsed = parseJson(script.textContent);
  /** @type {Record<string, string>} */
  const strings = {};
  if (!parsed || typeof parsed !== 'object') return strings;
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value === 'string') strings[key] = decodeEntities(value);
  }
  return strings;
}

/** @param {string | null} text */
function parseJson(text) {
  try {
    return JSON.parse(text || 'null');
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @returns {EncounterMini[] | null}
 */
function readCache(key) {
  try {
    const value = window.sessionStorage.getItem(key);
    if (!value) return null;
    const parsed = JSON.parse(value);
    if (!parsed || !Array.isArray(parsed.items)) return null;
    if (Date.now() - Number(parsed.savedAt) > CACHE_TTL_MS) return null;
    return parsed.items.map(normalizeMini).filter(Boolean);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {EncounterMini[]} items
 */
function writeCache(key, items) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), items }));
  } catch {
    // Storage can be full or blocked; the next visit just fetches again.
  }
}

/**
 * @param {string} name
 * @param {string} value
 */
function hiddenInput(name, value) {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = value;
  return input;
}

/**
 * @param {string} tag
 * @param {Record<string, string>} [attributes]
 */
function svgElement(tag, attributes = {}) {
  const element = document.createElementNS(SVG_NS, tag);
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
  return element;
}

/**
 * @param {string} src
 * @param {number} width
 */
function sizedImage(src, width) {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
}

/** @param {number} mm */
function formatMm(mm) {
  return Number.isInteger(mm) ? String(mm) : String(Math.round(mm * 10) / 10);
}

/** @param {number} n */
function f1(n) {
  return n.toFixed(1);
}

/** @param {number} n */
function f4(n) {
  return n.toFixed(4);
}

/**
 * Fisher-Yates on a copy.
 *
 * @template T
 * @param {T[]} items
 * @returns {T[]}
 */
function shuffle(items) {
  const copy = items.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = randomInt(0, i);
    const swap = /** @type {T} */ (copy[i]);
    copy[i] = /** @type {T} */ (copy[j]);
    copy[j] = swap;
  }
  return copy;
}

/**
 * Inclusive random integer.
 *
 * @param {number} min
 * @param {number} max
 */
function randomInt(min, max) {
  const span = max - min + 1;
  if (window.crypto?.getRandomValues) {
    const buffer = new Uint32Array(1);
    window.crypto.getRandomValues(buffer);
    return min + ((buffer[0] ?? 0) % span);
  }
  return min + Math.floor(Math.random() * span);
}

/**
 * Turns HTML entities (&#39;, &amp;) back into characters. A detached textarea parses its
 * content as plain text, so nothing in the string is ever treated as markup or run.
 *
 * @param {string} value
 */
function decodeEntities(value) {
  if (!value.includes('&')) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

/** @param {number} ms */
function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** @param {unknown} error */
function isAbortError(error) {
  return error instanceof DOMException && error.name === 'AbortError';
}

if (!customElements.get('sh-random-encounter-component')) {
  customElements.define('sh-random-encounter-component', RandomEncounterComponent);
}
